'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { pluralize, singularize } from 'inflected';
import indexBy from 'lodash.indexby';
import SmartFraction from '../Widgets/SmartFraction.react';
import LogPortionsModal from '../Planner/Modals/LogPortionsModal.react';

import { roundForHumans } from '../../utils/Math';
import { getNutrientsForMeals } from '../../utils/Nutrition';
import { getPrimaryMeal } from '../../utils/Meals';
import { isSingular } from '../../utils/Math';
import { servingIsVolume } from '../Foods/FoodUnitsSelector.react';
import Analytics from '../../utils/Analytics';

import './FoodUnitsSelectorModal.scss';

export default class FoodUnitsSelectorModal extends Component {
    static propTypes = {
        food: PropTypes.object,
        meal: PropTypes.object,
        howMuchQuestion: PropTypes.node,
        buttonText: PropTypes.string,
        replaceChevronText: PropTypes.string,
        nutrientsIndex: PropTypes.number,
        default_meal: PropTypes.bool
    };

    static defaultProps = {
        howMuchQuestion: "How much did you eat?",
    };

    static contextTypes = {
        user: PropTypes.object,
        location: PropTypes.object,
        router: PropTypes.object,
        isPro: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this.state = {
            nutrients: {},
            logPortionsFor: null,
            logPortionsParams: null,
        };
    }

    componentDidMount = () => {
        this.syncAssets();
    }

    syncAssets = () => {
        const { meal, food } = this.props;
        const { user } = this.context;

        const contents = indexBy([food], 'uuid');

        const { titles } = getPrimaryMeal([meal], contents, contents);

        const nutrients = getNutrientsForMeals([meal], contents, user?.portion || 1, null);

        this.setState({nutrients, titles});
    }

    UNSAFE_componentWillReceiveProps = (nextProps, nextContext) => {
        this.syncAssets();
    }

    openModal = () => {
        const { onChangeAmount, food, meal, howMuchQuestion, nutrientsIndex } = this.props;

        if (onChangeAmount) {
            // Flow 1: State-based (local modal)
            const defaultPortions = [];
            if (meal && food) {
                defaultPortions.push({
                    food_uuid: food.uuid,
                    logged_portion: meal.logged_portion,
                    logged_unit: meal.logged_unit,
                    logged_amount: meal.logged_amount,
                    logged_grams: meal.logged_grams,
                    logged_milliliters: meal.logged_milliliters,
                });
            }

            this.setState({
                logPortionsFor: [food],
                logPortionsParams: {
                    defaultPortions,
                    modalTitle: howMuchQuestion,
                }
            });
        } else {
            // Flow 2: Query param routing (deep linking support)
            const { location, router } = this.context;
            const { pathname, query, hash } = location;

            query.editPortionsMealUuid = meal.uuid;

            if (nutrientsIndex) {
                query.nutrientsIndex = nutrientsIndex;
            }

            router.push({pathname, query, hash});
        }
    }

    closeLogPortionsModal = () => {
        this.setState({logPortionsFor: null, logPortionsParams: null});
    }

    onSelectPortions = (contents, portions) => {
        const { onChangeAmount, food, meal, profile } = this.props;
        const portion = portions[food.uuid];

        if (!portion) {
            console.error('[FoodUnitsSelectorModal] No portion data returned');
            return;
        }

        // Track analytics for portion change
        Analytics.changeMealPortionSize({
            amount: portion.logged_amount,
            unit: portion.logged_unit,
            prevAmount: meal?.logged_amount,
            prevUnit: meal?.logged_unit,
            patient: profile?.type === 'patient' ? profile.uuid : null,
        });

        // Apply portion change
        if (onChangeAmount) {
            onChangeAmount(
                portion.logged_portion,
                portion.logged_unit,
                portion.logged_amount,
                portion.logged_grams,
                portion.logged_milliliters,
                portion.unit
            );
        }

        this.closeLogPortionsModal();
    }

    renderLogPortionsModal = () => {
        const { profile } = this.props;
        const { logPortionsFor, logPortionsParams } = this.state;

        if (!logPortionsFor) {
            return null;
        }

        const modalTitle = logPortionsParams?.modalTitle || "How much did you eat?";

        return (
            <LogPortionsModal
                closeModal={this.closeLogPortionsModal}
                profile={profile}
                modalTitle={modalTitle}
                ctaText="Ok"
                contents={logPortionsFor}
                defaultPortions={logPortionsParams?.defaultPortions}
                requirePrecise={false}
                onSelectPortions={this.onSelectPortions}
            />
        );
    }


    renderSpanContent = () => {
        const { food, buttonText } = this.props;
        let { logged_amount, logged_unit, logged_grams, logged_milliliters } = this.props.meal;
        const { user } = this.context;
        let suffix = null;

        if (logged_unit === 'serving') {
            // Show the measurement the content is actually described in. Preferring volume
            // whenever it exists would render a solid in fluid ounces, because a solid can
            // carry a milliliters_per_serving copied from its grams.
            const showVolume = servingIsVolume(food || {});

            if (showVolume && food?.milliliters_per_serving && user?.units_mode === 'english') {
                suffix = ' (' + roundForHumans(logged_amount * food?.milliliters_per_serving / 29.5735) + ' fl oz.)';
            } else if (showVolume && food?.milliliters_per_serving && user?.units_mode === 'metric') {
                suffix = ' (' + roundForHumans(logged_amount * food?.milliliters_per_serving) + ' ml)';
            } else if (food?.grams_per_serving && user?.units_mode === 'english') {
                suffix = ' (' + roundForHumans(logged_amount * food?.grams_per_serving / 28.3495) + ' oz)';
            } else if (food?.grams_per_serving && user?.units_mode === 'metric') {
                suffix = ' (' + roundForHumans(logged_amount * food?.grams_per_serving) + ' g)';
            }
        }

        logged_unit = isSingular(logged_amount) ? singularize(logged_unit) : pluralize(logged_unit);
        logged_unit = logged_unit || 'item';

        if (!buttonText) {
            if (logged_grams || logged_milliliters) {
                return (
                    <span className="read-only-food-units-selector">
                        <SmartFraction value={logged_amount} showZero={true} /> {logged_unit} {suffix}
                    </span>
                );
            } else if (logged_amount && !(logged_grams || logged_milliliters) && logged_unit) {
                return (
                    <span>
                        <SmartFraction value={logged_amount} showZero={true} /> {` `}
                        {logged_unit} {suffix}
                    </span>
                );
            } else if (logged_amount && !(logged_grams || logged_milliliters) && !logged_unit) {
                return (
                    <span className="read-only-food-units-selector">
                        <SmartFraction value={logged_amount} showZero={true} />
                        {isSingular(logged_amount) ? 'serving' : 'servings'} {suffix}
                    </span>
                );
            }
        }
        return buttonText ? <span className="read-only-food-units-selector">{buttonText}</span> : null;
    }

    render = () => {
        const { onChangeAmount, buttonText, replaceChevronText, readOnly = false } = this.props;
        let { logged_amount, logged_grams, logged_milliliters } = this.props.meal;
        const { isPro } = this.context;

        if (!(logged_grams || logged_milliliters) && !logged_amount) {
            return <span />
        }

        return (
            <div className="food-units-selector-outer">
                {readOnly ? <span style={{fontWeight: 400, cursor: 'default', pointerEvents: 'none'}}>{this.renderSpanContent()}</span> : null}

                {!readOnly ? (
                    <button className="open-modal-btn" onClick={this.openModal}>
                        {this.renderSpanContent()}
                        {!buttonText && !replaceChevronText && (!isPro || onChangeAmount) ? <i className="icon-chevron-down"/> : null}
                        {replaceChevronText ? <span className="replace-chevron-text">{replaceChevronText}</span> : null}
                    </button>
                ) : null}

                {!readOnly ? this.renderLogPortionsModal() : null}
            </div>
        );
    }
}
